import React, { useState, useEffect, useRef } from 'react';
import { X, Monitor, Smartphone, Tablet, Check } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import EditableRenderer from '../VisualEditor/EditableRenderer';

const TemplatePreviewModal = ({ templateNo, templateName, onClose, onSelect }) => {
  const [templateJSON, setTemplateJSON] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState('desktop');
  const canvasRef = useRef(null);

  useEffect(() => {
    const loadTemplate = async () => {
      setLoading(true);
      setError(null);
      try {
        let data;
        try {
          const res = await axios.get(`/static/template_bases/template_${templateNo}.json`);
          data = res.data;
        } catch {
          const res = await axios.get(`${API_BASE_URL}/static/template_bases/template_${templateNo}.json`);
          data = res.data;
        }
        if (data && data.blocks) {
          setTemplateJSON(data);
        } else {
          setError('Invalid template data');
        }
      } catch (err) {
        setError('Could not load template preview');
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [templateNo]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const deviceWidths = { desktop: '100%', tablet: '768px', mobile: '375px' };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{
        height: 56, background: '#ffffff', borderBottom: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
            {templateName || `Template ${templateNo}`}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 500, color: '#6b7280',
            background: '#f3f4f6', padding: '2px 8px', borderRadius: 4,
          }}>
            Preview
          </span>
        </div>

        {/* Device toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', background: '#f3f4f6',
          borderRadius: 10, padding: 3,
        }}>
          {[
            { d: 'desktop', Icon: Monitor, label: 'Desktop' },
            { d: 'tablet', Icon: Tablet, label: 'Tablet' },
            { d: 'mobile', Icon: Smartphone, label: 'Mobile' },
          ].map(({ d, Icon, label }) => {
            const isActive = device === d;
            return (
              <button
                key={d}
                onClick={() => setDevice(d)}
                style={{
                  width: 34, height: 30, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', borderRadius: 7, border: 'none',
                  cursor: 'pointer',
                  background: isActive ? '#fff' : 'transparent',
                  color: isActive ? '#3b82f6' : '#9ca3af',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                }}
                title={label}
              >
                <Icon style={{ width: 16, height: 16 }} />
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onSelect && (
            <button
              onClick={() => onSelect(templateNo)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 20px', borderRadius: 10, border: 'none',
                background: '#111827', color: '#fff', fontSize: 13,
                fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1e293b'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#111827'; }}
            >
              <Check style={{ width: 14, height: 14 }} />
              Select This Template
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: 8, border: '1px solid #e5e7eb',
              background: '#fafafa', cursor: 'pointer', color: '#6b7280',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      {/* Preview canvas */}
      <div style={{
        flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center',
        padding: device === 'desktop' ? '0' : '20px',
        background: device === 'desktop' ? '#fff' : '#e8eaed',
      }}>
        {loading && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '100%', color: '#9ca3af',
          }}>
            <div style={{
              width: 32, height: 32, border: '3px solid #e5e7eb',
              borderTopColor: '#6366f1', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
          </div>
        )}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '100%', color: '#ef4444', fontSize: 14,
          }}>
            {error}
          </div>
        )}
        {!loading && !error && templateJSON && (
          <div
            ref={canvasRef}
            style={{
              width: deviceWidths[device],
              maxWidth: deviceWidths[device],
              minHeight: '100%',
              background: '#ffffff',
              boxShadow: device !== 'desktop' ? '0 4px 24px rgba(0,0,0,0.12)' : 'none',
              borderRadius: device !== 'desktop' ? '12px' : '0',
              overflow: 'hidden',
              transition: 'width 0.3s ease, max-width 0.3s ease',
            }}
          >
            <EditableRenderer
              templateJSON={templateJSON}
              editMode={false}
              previewDevice={device}
              onContentChange={() => {}}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TemplatePreviewModal;
